<template>
  <ExpensesPageShell
    title="Gastos"
    subtitle="Gestión de gastos y control financiero"
    :actions="headerActions"
    @action="onHeaderAction"
  >
    <template #actions="{ onAction }">
      <div class="app-page-header-actions expenses-header-actions">
        <button
          v-for="action in headerActions"
          :key="action.key"
          class="app-header-btn"
          :class="`app-header-btn--${action.variant || 'primary'}`"
          type="button"
          @click="onAction(action)"
        >
          <i v-if="action.icon" class="fa" :class="action.icon" aria-hidden="true"></i>
          <span>{{ action.label }}</span>
        </button>
      </div>
    </template>

    <template #metrics>
      <section class="metrics-grid">
        <SalesKpiCard
          v-for="metric in responsiveKpiCards"
          :key="metric.title"
          :title="metric.title"
          :value="metric.value"
          :value-route="metric.valueRoute"
          :value-link-class="metric.valueLinkClass"
          :value-scale="metric.valueScale"
          :truncate-value="metric.truncateValue"
          :value-meta="metric.valueMeta"
          :value-meta-lines="metric.valueMetaLines"
          :extra-values="metric.extraValues"
          :value-segments="metric.valueSegments"
          :icon="metric.icon"
          :icon-bg="metric.iconBg"
          :icon-color="metric.iconColor"
        />
      </section>
    </template>

    <template #toolbar>
      <ExpensesToolbar
        :filter-options="filterOptions"
        @search-change="handleToolbarSearch"
        @filters-change="handleToolbarFilters"
        @apply="applyToolbarFilters"
      />

      <StatusTabs
        :tabs="expenseStatusTabs"
        :active-tab="activeStatusTab"
        @select="selectStatusTab"
      />
    </template>

    <template #table>
      <ExpensesTable
        :rows="paginatedRows"
        :loading="tableLoading"
        :pagination="expensesPagination"
        :use-external-sort="true"
        :sort-field="sortField"
        :sort-direction="sortDirection"
        empty-text="Sin gastos disponibles para los filtros seleccionados."
        @page-change="handlePageChange"
        @sort-change="handleSortChange"
        @pay-expense="handlePayExpense"
        @edit-expense="handleEditExpense"
        @delete-expense="handleDeleteExpense"
        @approve-expense="handleApproveExpense"
        @download-xml="handleDownloadXml"
        @download-pdf="handleDownloadPdf"
        @download-receipt="handleDownloadReceipt"
      />
    </template>

    <template #modal>
      <ExpenseModal
        :show="isExpenseModalOpen"
        :mode="expenseModalMode"
        :initial-data="expenseModalInitialData"
        :submitting="isExpenseSubmitting"
        @close="closeExpenseModal"
        @submit="submitExpense"
      />

      <ConfirmActionModal
        :show="isDeleteExpenseModalOpen"
        title="Eliminar gasto"
        subtitle="Esta acción no se puede deshacer."
        :message="deleteExpenseModalMessage"
        :confirm-text="deleteExpenseConfirmText"
        cancel-text="Cancelar"
        @cancel="closeDeleteExpenseModal"
        @confirm="confirmDeleteExpense"
      />

      <ConfirmActionModal
        :show="isApproveExpenseModalOpen"
        :title="approveExpenseModalTitle"
        :subtitle="approveExpenseModalSubtitle"
        :message="approveExpenseModalMessage"
        :confirm-text="approveExpenseConfirmText"
        cancel-text="Cancelar"
        @cancel="closeApproveExpenseModal"
        @confirm="confirmApproveExpense"
      />

      <PayModal
        :show="isPayExpenseModalOpen"
        :row="payExpenseTarget"
        context="expense-fee"
        entity-type="expense"
        :submitting="isPayExpenseSubmitting"
        @close="closePayExpenseModal"
        @submit="submitPayExpense"
      />
    </template>
  </ExpensesPageShell>
</template>

<script>
import ExpensesPageShell from '@/components/expenses/ExpensesPageShell.vue';
import ExpensesToolbar from '@/components/expenses/ExpensesToolbar.vue';
import ExpensesTable from '@/components/expenses/ExpensesTable.vue';
import ExpenseModal from '@/components/expenses/ExpenseModal.vue';
import PayModal from '@/components/pay/modal/PayModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import SalesKpiCard from '@/components/sales/SalesKpiCard.vue';
import StatusTabs from '@/components/common/StatusTabs.vue';
import { formatCurrency } from '@/utils/numberFormat';
import {
  buildExpenseFeeListParams as buildExpensesListParams,
  createDefaultExpenseFeeToolbarFilters as createDefaultExpensesToolbarFilters,
  normalizeExpenseFeeToolbarFilters as normalizeExpensesToolbarFilters,
} from '@/composables/expensesFees/shared/useExpenseFeeFilters';
import {
  buildExpenseFeeStatusTabs as buildExpenseStatusTabs,
  filterExpenseFeeByStatus as filterExpensesByStatus,
} from '@/composables/expensesFees/shared/useExpenseFeeStatusTabs';
import {
  fetchExpensesSnapshot,
  getExpensesFilterOptions,
  fetchExpensesFilterOptions,
} from '@/composables/expenses/useExpensesList';
import {
  createInitialExpensesMetricsValues,
  mapExpensesMetricsPayload,
  buildExpensesKpiCards,
} from '@/composables/expenses/useExpensesMetrics';
import {
  confirmExpenseApproveFlow,
  confirmExpenseDeleteFlow,
  closeExpenseModalState,
  notifyExpenseActionPending,
  openExpenseEditModalFromRow,
  openExpenseModalState,
  resolveExpenseApprovalTargetState,
  submitExpenseFlow,
  submitExpensePayFlow,
} from '@/composables/expenses/useExpenseFlow';
import {
  resolveTotalPages,
  ensureValidPage,
  sliceRowsByPage,
  buildPaginationPayload,
} from '@/composables/shared/usePagination';
import {
  resolveSortChange,
  sortRows,
} from '@/composables/shared/useSorting';
import { EXPENSES_TEXT } from '@/texts/catalog/expenses';

const EXPENSES_FLOW_TEXT = EXPENSES_TEXT.flow;

export default {
  name: 'ExpensesPage',
  SMALL_SCREEN_BREAKPOINT: 1360,
  MILLION_THRESHOLD: 1000000,
  components: {
    ExpensesPageShell,
    ExpensesToolbar,
    ExpensesTable,
    ExpenseModal,
    PayModal,
    ConfirmActionModal,
    SalesKpiCard,
    StatusTabs,
  },
  data() {
    return {
      headerActions: [
        {
          key: 'new-expense',
          label: 'Nuevo gasto',
          icon: 'fa-plus',
          variant: 'primary',
        },
        {
          key: 'import-expenses',
          label: 'Importar gastos',
          icon: 'fa-upload',
          variant: 'secondary',
        },
      ],
      tableLoading: false,
      expenseRows: [],
      toolbarSearch: '',
      toolbarFilters: createDefaultExpensesToolbarFilters(),
      appliedToolbarSearch: '',
      appliedToolbarFilters: createDefaultExpensesToolbarFilters(),
      activeStatusTab: 'all',
      sortField: 'date',
      sortDirection: 'asc',
      listRequestToken: 0,
      currentPage: 1,
      pageSize: 20,
      metricsValues: createInitialExpensesMetricsValues(),
      filterOptions: getExpensesFilterOptions(),
      isExpenseModalOpen: false,
      isExpenseSubmitting: false,
      expenseModalMode: 'create',
      expenseModalInitialData: null,
      isDeleteExpenseModalOpen: false,
      isDeletingExpense: false,
      deleteExpenseTarget: null,
      isApproveExpenseModalOpen: false,
      isApprovingExpense: false,
      approveExpenseTarget: null,
      approveExpenseTargetState: true,
      isPayExpenseModalOpen: false,
      isPayExpenseSubmitting: false,
      payExpenseTarget: null,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1920,
    };
  },
  computed: {
    normalizedRows() {
      return Array.isArray(this.expenseRows) ? this.expenseRows : [];
    },
    expenseStatusTabs() {
      return buildExpenseStatusTabs(this.searchedRows);
    },
    searchedRows() {
      const search = String(this.toolbarSearch || '').trim().toLowerCase();
      if (search === '') {
        return this.normalizedRows;
      }

      return this.normalizedRows.filter((row) => {
        const haystack = [
          row?.provider,
          row?.concept,
          row?.folio,
          row?.reference,
          row?.customer,
          row?.sale_name,
          row?.sale_code,
        ]
          .map((value) => String(value || '').trim().toLowerCase())
          .join(' ');

        return haystack.includes(search);
      });
    },
    filteredRows() {
      return filterExpensesByStatus(this.searchedRows, this.activeStatusTab);
    },
    sortedRows() {
      return sortRows(this.filteredRows, {
        field: this.sortField,
        direction: this.sortDirection,
      }, {
        date: (row) => new Date(String(row?.date || '')).getTime() || 0,
        provider: (row) => String(row?.provider || '').trim(),
        subtotal: (row) => Number(row?.subtotal || 0),
        total: (row) => Number(row?.total || 0),
        payment_date: (row) => new Date(String(row?.payment_date || '')).getTime() || 0,
      });
    },
    totalPages() {
      return resolveTotalPages(this.sortedRows.length, this.pageSize);
    },
    paginatedRows() {
      return sliceRowsByPage(this.sortedRows, this.currentPage, this.pageSize);
    },
    expensesPagination() {
      return buildPaginationPayload({
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        totalItems: this.sortedRows.length,
      });
    },
    kpiCards() {
      return buildExpensesKpiCards(this.metricsValues);
    },
    isSmallScreen() {
      return Number(this.viewportWidth || 0) > 0
        && Number(this.viewportWidth || 0) <= this.$options.SMALL_SCREEN_BREAKPOINT;
    },
    responsiveKpiCards() {
      if (!Array.isArray(this.kpiCards) || this.kpiCards.length === 0) {
        return [];
      }

      const responsiveScales = [
        this.resolveResponsiveExpenseKpiScale(this.metricsValues?.total_expenses),
        this.resolveResponsiveExpenseKpiScale(this.metricsValues?.pending_to_pay),
        this.resolveResponsiveExpenseKpiScale(this.metricsValues?.paid),
      ];

      return this.kpiCards.map((metric, index) => {
        const nextMetric = {
          ...metric,
        };

        if (index <= 2) {
          const nextScale = responsiveScales[index];
          if (nextScale !== null) {
            nextMetric.valueScale = nextScale;
          }
        }

        return nextMetric;
      });
    },
    deleteExpenseModalMessage() {
      const row = this.deleteExpenseTarget;
      if (!row || typeof row !== 'object') {
        return 'Confirma que deseas eliminar este gasto.';
      }

      const provider = String(row?.provider || '').trim() || 'Sin proveedor';
      const concept = String(row?.concept || '').trim() || 'Sin concepto';
      const amount = formatCurrency(row?.total ?? row?.amount ?? 0, {
        currency: String(row?.currency || 'MXN').trim().toUpperCase() || 'MXN',
      });

      return `Proveedor: ${provider}\nConcepto: ${concept}\nMonto: ${amount}`;
    },
    deleteExpenseConfirmText() {
      return this.isDeletingExpense ? 'Eliminando...' : 'Eliminar';
    },
    approveExpenseModalTitle() {
      return this.approveExpenseTargetState ? 'Aprobar gasto' : 'No aprobar gasto';
    },
    approveExpenseModalSubtitle() {
      return this.approveExpenseTargetState
        ? 'Confirma para marcar este gasto como aprobado.'
        : 'Confirma para quitar la aprobación de este gasto.';
    },
    approveExpenseModalMessage() {
      const row = this.approveExpenseTarget;
      if (!row || typeof row !== 'object') {
        return this.approveExpenseTargetState
          ? 'Confirma que deseas aprobar este gasto.'
          : 'Confirma que deseas no aprobar este gasto.';
      }

      const provider = String(row?.provider || '').trim() || 'Sin proveedor';
      const concept = String(row?.concept || '').trim() || 'Sin concepto';
      const amount = formatCurrency(row?.total ?? row?.amount ?? 0, {
        currency: String(row?.currency || 'MXN').trim().toUpperCase() || 'MXN',
      });

      return `Proveedor: ${provider}\nConcepto: ${concept}\nMonto: ${amount}`;
    },
    approveExpenseConfirmText() {
      if (this.isApprovingExpense) {
        return this.approveExpenseTargetState ? 'Aprobando...' : 'Actualizando...';
      }

      return this.approveExpenseTargetState ? 'Aprobar' : 'No aprobar';
    },
  },
  mounted() {
    this.loadFilterOptions();
    if (this.applySupplierFilterFromRoute()) {
      this.fetchExpenses();
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleWindowResize);
    }
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleWindowResize);
    }
  },
  methods: {
    handleWindowResize() {
      this.viewportWidth = typeof window !== 'undefined' ? window.innerWidth : this.viewportWidth;
    },
    resolveResponsiveExpenseKpiScale(amount) {
      const numericAmount = Math.abs(Number(amount || 0));
      if (!this.isSmallScreen || numericAmount < this.$options.MILLION_THRESHOLD) {
        return null;
      }

      return 0.82;
    },
    onHeaderAction(action) {
      const key = String(action?.key || '').trim();

      if (key === 'new-expense') {
        this.openExpenseModal();
        return;
      }

      if (key === 'import-expenses') {
        this.$toast?.info('La importación de gastos se conectará en el siguiente paso.');
      }
    },
    openExpenseModal(mode = 'create', initialData = null) {
      openExpenseModalState(this, mode, initialData);
    },
    closeExpenseModal() {
      closeExpenseModalState(this);
    },
    closeDeleteExpenseModal() {
      if (this.isDeletingExpense) {
        return;
      }

      this.isDeleteExpenseModalOpen = false;
      this.deleteExpenseTarget = null;
    },
    closeApproveExpenseModal() {
      if (this.isApprovingExpense) {
        return;
      }

      this.isApproveExpenseModalOpen = false;
      this.approveExpenseTarget = null;
      this.approveExpenseTargetState = true;
    },
    closePayExpenseModal() {
      if (this.isPayExpenseSubmitting) {
        return;
      }

      this.isPayExpenseModalOpen = false;
      this.isPayExpenseSubmitting = false;
      this.payExpenseTarget = null;
    },
    async submitPayExpense(payload = {}) {
      await submitExpensePayFlow(this, payload, {
        onSuccess: async () => {
          await this.fetchExpenses();
        },
      });
    },
    resolveApprovalTargetState(row, explicitState = null) {
      return resolveExpenseApprovalTargetState(row, explicitState);
    },
    async submitExpense(payload) {
      await submitExpenseFlow(this, payload, {
        onSuccess: async () => {
          await this.fetchExpenses();
        },
      });
    },
    ensureValidPage() {
      this.currentPage = ensureValidPage(this.currentPage, this.totalPages);
    },
    buildListParams() {
      return buildExpensesListParams({
        toolbarFilters: this.appliedToolbarFilters,
        toolbarSearch: this.appliedToolbarSearch,
      });
    },
    selectStatusTab(tabKey) {
      const normalized = String(tabKey || '').trim();
      if (normalized === '' || normalized === this.activeStatusTab) {
        return;
      }

      this.activeStatusTab = normalized;
      this.currentPage = 1;
    },
    async loadFilterOptions() {
      try {
        this.filterOptions = await fetchExpensesFilterOptions();
      } catch (error) {
        this.filterOptions = getExpensesFilterOptions();
      }
    },
    applySupplierFilterFromRoute() {
      const supplierId = Number(this.$route?.query?.supplier_id || 0);
      if (!Number.isFinite(supplierId) || supplierId <= 0) {
        return false;
      }

      const nextFilters = normalizeExpensesToolbarFilters({
        ...this.toolbarFilters,
        provider: [supplierId],
      });

      this.toolbarFilters = nextFilters;
      this.appliedToolbarFilters = nextFilters;
      this.currentPage = 1;

      return true;
    },
    async fetchExpenses() {
      const requestToken = ++this.listRequestToken;
      this.tableLoading = true;

      try {
        const snapshot = await fetchExpensesSnapshot(this.buildListParams());

        if (requestToken === this.listRequestToken) {
          const rows = Array.isArray(snapshot?.rows) ? snapshot.rows : [];
          this.expenseRows = rows;
          this.metricsValues = mapExpensesMetricsPayload(snapshot?.metrics || {});
          this.ensureValidPage();
        }
      } catch (error) {
        if (requestToken === this.listRequestToken) {
          this.expenseRows = [];
          this.metricsValues = createInitialExpensesMetricsValues();
        }
      } finally {
        if (requestToken === this.listRequestToken) {
          this.tableLoading = false;
        }
      }
    },
    handleToolbarSearch(value) {
      const nextSearch = String(value || '');
      this.toolbarSearch = nextSearch;
      this.appliedToolbarSearch = nextSearch;
      this.currentPage = 1;
    },
    handleToolbarFilters(filters, meta = {}) {
      const previousDateStart = String(this.toolbarFilters?.date_start || '').trim();
      const previousDateEnd = String(this.toolbarFilters?.date_end || '').trim();
      const normalized = normalizeExpensesToolbarFilters(filters);
      this.toolbarFilters = normalized;

      if (Object.prototype.hasOwnProperty.call(normalized, 'search')) {
        this.toolbarSearch = String(normalized.search || '');
      }

      const nextDateStart = String(normalized.date_start || '').trim();
      const nextDateEnd = String(normalized.date_end || '').trim();
      const dateChanged = nextDateStart !== previousDateStart || nextDateEnd !== previousDateEnd;
      const trigger = String(meta?.trigger || '').trim();

      if (dateChanged && trigger === 'date') {
        this.appliedToolbarFilters = normalized;
        this.appliedToolbarSearch = String(this.toolbarSearch || '');
        this.currentPage = 1;
        this.fetchExpenses();
      }
    },
    applyToolbarFilters(payload = {}) {
      const search = Object.prototype.hasOwnProperty.call(payload, 'search')
        ? String(payload.search || '')
        : String(this.toolbarSearch || '');

      const filters = Object.prototype.hasOwnProperty.call(payload, 'filters')
        ? normalizeExpensesToolbarFilters(payload.filters)
        : normalizeExpensesToolbarFilters(this.toolbarFilters);

      this.toolbarSearch = search;
      this.toolbarFilters = filters;
      this.appliedToolbarSearch = search;
      this.appliedToolbarFilters = filters;
      this.currentPage = 1;
      this.fetchExpenses();
    },
    handlePageChange(nextPage) {
      this.currentPage = Number(nextPage || 1);
      this.ensureValidPage();
    },
    handleSortChange(payload) {
      const nextSort = resolveSortChange(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        payload,
        ['date', 'provider', 'subtotal', 'total', 'payment_date'],
      );

      if (nextSort.field === this.sortField && nextSort.direction === this.sortDirection) {
        return;
      }

      this.sortField = nextSort.field;
      this.sortDirection = nextSort.direction;
      this.currentPage = 1;
    },
    handlePayExpense(row) {
      if (this.isPayExpenseSubmitting) {
        return;
      }

      this.payExpenseTarget = row && typeof row === 'object' ? { ...row } : null;
      this.isPayExpenseModalOpen = true;
    },
    handleEditExpense(row) {
      openExpenseEditModalFromRow(this, row, {
        mapInitialData: ({ detail, rowData }) => {
          const mergedPayload = {
            ...rowData,
            ...detail,
          };

          if (String(mergedPayload.status || '').trim() === '') {
            mergedPayload.status = String(rowData.status || '').trim();
          }

          if (Number(mergedPayload.payment_id || 0) <= 0) {
            mergedPayload.payment_id = Number(
              detail.payment_id
              || rowData.payment_id
              || rowData.payment?.id
              || 0
            ) || 0;
          }

          if (mergedPayload.is_approved === undefined || mergedPayload.is_approved === null) {
            mergedPayload.is_approved = rowData.is_approved;
          }

          return mergedPayload;
        },
      });
    },
    handleApproveExpense(row, nextApproved = null) {
      this.approveExpenseTarget = row && typeof row === 'object' ? { ...row } : null;
      this.approveExpenseTargetState = this.resolveApprovalTargetState(row, nextApproved);
      this.isApproveExpenseModalOpen = true;
    },
    handleDeleteExpense(row) {
      this.deleteExpenseTarget = row && typeof row === 'object' ? { ...row } : null;
      this.isDeleteExpenseModalOpen = true;
    },
    async confirmDeleteExpense() {
      await confirmExpenseDeleteFlow(this, {
        onSuccess: async () => {
          await this.fetchExpenses();
        },
      });
    },
    async confirmApproveExpense() {
      await confirmExpenseApproveFlow(this, {
        onSuccess: async () => {
          await this.fetchExpenses();
        },
      });
    },
    handleDownloadXml(row) {
      notifyExpenseActionPending(this, row, { actionLabel: EXPENSES_FLOW_TEXT.pending.actions.downloadXml });
    },
    handleDownloadPdf(row) {
      notifyExpenseActionPending(this, row, { actionLabel: EXPENSES_FLOW_TEXT.pending.actions.downloadPdf });
    },
    handleDownloadReceipt(row) {
      notifyExpenseActionPending(this, row, { actionLabel: EXPENSES_FLOW_TEXT.pending.actions.downloadReceipt });
    },
  },
};
</script>
